import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NavbarProps {
  onProgettoClick: () => void;
  transparent?: boolean;
}

const LANGS = ['IT', 'EN', 'DE'] as const;
type LangCode = typeof LANGS[number];
const langMap: Record<LangCode, string> = { IT: 'it', EN: 'en', DE: 'de' };

const Navbar = ({ onProgettoClick, transparent = false }: NavbarProps) => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === '/';
  const useTransparent = transparent || isHome;

  const currentLang = (Object.entries(langMap).find(([, v]) => v === i18n.language)?.[0] ?? 'IT') as LangCode;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setScrolled(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectLang = (lang: LangCode) => {
    i18n.changeLanguage(langMap[lang]);
    setLangOpen(false);
  };

  const cycleLang = () => {
    const idx = LANGS.indexOf(currentLang);
    const next = LANGS[(idx + 1) % LANGS.length];
    i18n.changeLanguage(langMap[next]);
  };

  const isScrolledOrOpaque = scrolled || !useTransparent;

  const baseBtn = isScrolledOrOpaque
    ? 'border-terracotta-300 text-terracotta-300 hover:bg-terracotta-300 hover:text-white'
    : 'border-white/70 text-white hover:bg-white/20';

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolledOrOpaque
            ? 'bg-ivory-50/95 backdrop-blur-sm shadow-sm py-3'
            : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <a
            href="/"
            onClick={handleLogoClick}
            className="flex items-center gap-3 cursor-pointer"
          >
            <img
              src={
                isScrolledOrOpaque
                  ? "https://storage.readdy-site.link/project_files/eb454c64-f721-4c2b-844b-992754102f07/68028622-5d7c-4d52-9660-5d8c18db25c4_logo_marchecasa-2_v.png?v=cf5ee50c6e66d47ffb4a65af1160c07b"
                  : "https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/d566f37bb7c274eb292da4befb3f457a.webp"
              }
              alt="Marche Casa Logo"
              className="h-10 w-auto object-contain transition-all duration-500"
            />
            <div className="flex flex-col leading-tight">
              <span className={`font-serif text-base font-medium tracking-wide transition-colors duration-300 whitespace-nowrap ${
                isScrolledOrOpaque ? 'text-anthracite-800' : 'text-white'
              }`}>
                Marche Casa
              </span>
              <span
                className={`font-sans text-[10px] tracking-widest uppercase transition-colors duration-300 whitespace-nowrap ${
                  isScrolledOrOpaque ? '' : 'text-white'
                }`}
                style={{ color: isScrolledOrOpaque ? '#508B14' : undefined }}
              >
                DISCOVER Your Dream Home
              </span>
            </div>
          </a>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/borghi"
              className={`font-sans text-sm tracking-widest uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                isScrolledOrOpaque
                  ? location.pathname === '/borghi'
                    ? 'text-terracotta-300'
                    : 'text-anthracite-600 hover:text-terracotta-300'
                  : location.pathname === '/borghi'
                    ? 'text-terracotta-200'
                    : 'text-white/90 hover:text-white'
              }`}
            >
              {t('nav.borghi')}
            </Link>
            <Link
              to="/servizi"
              className={`font-sans text-sm tracking-widest uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                isScrolledOrOpaque
                  ? location.pathname === '/servizi'
                    ? 'text-terracotta-300'
                    : 'text-anthracite-600 hover:text-terracotta-300'
                  : location.pathname === '/servizi'
                    ? 'text-terracotta-200'
                    : 'text-white/90 hover:text-white'
              }`}
            >
              {t('nav.servizi')}
            </Link>
            <Link
              to="/partner"
              className={`font-sans text-sm tracking-widest uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                isScrolledOrOpaque
                  ? location.pathname.startsWith('/partner')
                    ? 'text-terracotta-300'
                    : 'text-anthracite-600 hover:text-terracotta-300'
                  : location.pathname.startsWith('/partner')
                    ? 'text-terracotta-200'
                    : 'text-white/90 hover:text-white'
              }`}
            >
              Partner
            </Link>
            <a
              href="https://www.discoveringmarche.com/blog/"
              rel="nofollow"
              className={`font-sans text-sm tracking-widest uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                isScrolledOrOpaque
                  ? 'text-anthracite-600 hover:text-terracotta-300'
                  : 'text-white/90 hover:text-white'
              }`}
            >
              Blog
            </a>
            <Link
              to="/progetto"
              className={`font-sans text-sm tracking-widest uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                isScrolledOrOpaque
                  ? location.pathname === '/progetto'
                    ? 'text-terracotta-300'
                    : 'text-anthracite-600 hover:text-terracotta-300'
                  : location.pathname === '/progetto'
                    ? 'text-terracotta-200'
                    : 'text-white/90 hover:text-white'
              }`}
            >
              {t('nav.progetto')}
            </Link>
            <Link
              to="/contatti"
              className={`font-sans text-sm tracking-widest uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                isScrolledOrOpaque
                  ? location.pathname === '/contatti'
                    ? 'text-terracotta-300'
                    : 'text-anthracite-600 hover:text-terracotta-300'
                  : location.pathname === '/contatti'
                    ? 'text-terracotta-200'
                    : 'text-white/90 hover:text-white'
              }`}
            >
              {t('nav.contatti')}
            </Link>
            <a
              href="https://www.discoveringmarche.com/#recensioni"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className={`font-sans text-sm tracking-widest uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                isScrolledOrOpaque
                  ? 'text-anthracite-600 hover:text-terracotta-300'
                  : 'text-white/90 hover:text-white'
              }`}
            >
              Recensioni
            </a>
          </div>

          {/* Right actions */}
          <div className="hidden md:flex items-center gap-4">
            {/* Lang Dropdown */}
            <div ref={langRef} className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-sans tracking-widest uppercase transition-all duration-300 cursor-pointer whitespace-nowrap ${baseBtn}`}
              >
                <span>{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {langOpen && (
                <div className={`absolute right-0 top-full mt-2 rounded-lg overflow-hidden border py-1 min-w-[80px] ${
                  isScrolledOrOpaque
                    ? 'bg-ivory-50 border-ivory-200'
                    : 'bg-anthracite-800/90 backdrop-blur-sm border-white/10'
                }`}>
                  {LANGS.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => selectLang(lang)}
                      className={`w-full text-left px-4 py-2 font-sans text-xs tracking-widest uppercase transition-colors cursor-pointer whitespace-nowrap ${
                        lang === currentLang
                          ? isScrolledOrOpaque
                            ? 'text-terracotta-300 bg-terracotta-50'
                            : 'text-terracotta-300 bg-white/10'
                          : isScrolledOrOpaque
                            ? 'text-anthracite-600 hover:text-terracotta-300 hover:bg-ivory-100'
                            : 'text-white/70 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Instagram */}
            <a
              href="https://www.instagram.com/felicity.dinallo_/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className={`w-8 h-8 flex items-center justify-center rounded-full border transition-all duration-300 cursor-pointer ${
                isScrolledOrOpaque
                  ? 'border-anthracite-400 text-anthracite-500 hover:border-terracotta-300 hover:text-terracotta-300'
                  : 'border-white/60 text-white/80 hover:border-white hover:text-white'
              }`}
              aria-label="Instagram"
            >
              <i className="ri-instagram-line text-sm"></i>
            </a>


          </div>

          {/* Mobile hamburger */}
          <div className="flex md:hidden items-center gap-3">
            <button
              onClick={cycleLang}
              className={`text-xs font-sans px-2 py-1 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                isScrolledOrOpaque ? 'border-terracotta-300 text-terracotta-300' : 'border-white/60 text-white'
              }`}
            >
              {currentLang}
            </button>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`w-8 h-8 flex items-center justify-center cursor-pointer ${
                isScrolledOrOpaque ? 'text-anthracite-700' : 'text-white'
              }`}
              aria-label="Menu"
            >
              <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-400 ${
          menuOpen ? 'visible' : 'invisible'
        }`}
      >
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity duration-400 ${
            menuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMenuOpen(false)}
        />
        <div
          className={`absolute right-0 top-0 h-full w-72 bg-ivory-50 shadow-xl transition-transform duration-400 ${
            menuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full pt-20 pb-8 px-8">
            {/* Mobile lang switcher */}
            <div className="flex items-center gap-2 mb-8">
              {LANGS.map((lang) => (
                <button
                  key={lang}
                  onClick={() => selectLang(lang)}
                  className={`px-3 py-1 rounded-full text-xs font-sans tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap border ${
                    lang === currentLang
                      ? 'bg-terracotta-300 border-terracotta-300 text-white'
                      : 'border-anthracite-300 text-anthracite-500 hover:border-terracotta-300 hover:text-terracotta-300'
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-6 flex-1">
              <Link
                to="/borghi"
                onClick={() => setMenuOpen(false)}
                className={`font-serif text-2xl transition-colors cursor-pointer ${
                  location.pathname === '/borghi'
                    ? 'text-terracotta-300'
                    : 'text-anthracite-700 hover:text-terracotta-300'
                }`}
              >
                {t('nav.borghi')}
              </Link>
              <Link
                to="/servizi"
                onClick={() => setMenuOpen(false)}
                className={`font-serif text-2xl transition-colors cursor-pointer ${
                  location.pathname === '/servizi'
                    ? 'text-terracotta-300'
                    : 'text-anthracite-700 hover:text-terracotta-300'
                }`}
              >
                {t('nav.servizi')}
              </Link>
              <Link
                to="/partner"
                onClick={() => setMenuOpen(false)}
                className={`font-serif text-2xl transition-colors cursor-pointer ${
                  location.pathname.startsWith('/partner')
                    ? 'text-terracotta-300'
                    : 'text-anthracite-700 hover:text-terracotta-300'
                }`}
              >
                Partner
              </Link>
              <a
                href="https://www.discoveringmarche.com/blog/"
                rel="nofollow"
                onClick={() => setMenuOpen(false)}
                className="font-serif text-2xl transition-colors cursor-pointer text-anthracite-700 hover:text-terracotta-300"
              >
                Blog
              </a>
              <Link
                to="/progetto"
                onClick={() => setMenuOpen(false)}
                className={`font-serif text-2xl transition-colors cursor-pointer ${
                  location.pathname === '/progetto'
                    ? 'text-terracotta-300'
                    : 'text-anthracite-700 hover:text-terracotta-300'
                }`}
              >
                {t('nav.progetto')}
              </Link>
              <Link
                to="/contatti"
                onClick={() => setMenuOpen(false)}
                className={`font-serif text-2xl transition-colors cursor-pointer ${
                  location.pathname === '/contatti'
                    ? 'text-terracotta-300'
                    : 'text-anthracite-700 hover:text-terracotta-300'
                }`}
              >
                {t('nav.contatti')}
              </Link>
              <a
                href="https://www.discoveringmarche.com/#recensioni"
                target="_blank"
                rel="nofollow noopener noreferrer"
                onClick={() => setMenuOpen(false)}
                className="font-serif text-2xl transition-colors cursor-pointer text-anthracite-700 hover:text-terracotta-300"
              >
                Recensioni
              </a>
            </div>
            <div className="flex items-center gap-3 mt-8">
              <a
                href="https://www.instagram.com/felicity.dinallo_/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full border border-anthracite-300 text-anthracite-500 hover:border-terracotta-300 hover:text-terracotta-300 transition-all cursor-pointer"
              >
                <i className="ri-instagram-line"></i>
              </a>
              <span className="font-sans text-xs text-anthracite-400 tracking-widest">@felicity.dinallo_</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
